import { useGetHasRedundantEnrollments } from '@actions';
import HeaderV3 from '@components/V3/HeaderV3';
import AgoraModal from '@components/V3/Utils/AgoraModal';
import { useAuthState } from '@contexts/AuthContext';
import AccountPage from '@modules/Account';
import KnowledgeBaseAssistant from '@modules/knowledgeBaseAssistant/components/KnowledgeBaseAssistant';
import {
  ACCOUNT,
  HOME,
  COURSES,
  MY_ENROLLMENTS,
  SESSIONS,
  KNOWLEDGE_BASE_ASSISTANT,
  VIDEO_MEETING,
  STUDENT_PROFILE,
  SESSION_DETAILS,
  MENTOR_ACTIVITY_REPORT,
  PAST_STUDENTS,
  MY_FLAGS,
  PARENT_FEEDBACK,
} from '@routes';
import { Button } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, Redirect, Route, Switch } from 'react-router-dom';
import { COLORS } from '../../v3/global';
import Courses from './Courses/Courses';
import MentorHome from './Home/Mentor';
import Home from './Home/Student';

import './user-view.styles.scss';
import ActivityReport from './MentorView/ActivityReport';
import MyEnrollments from './MentorView/MyEnrollments';
import MyFlags from './MentorView/MyFlags';
import PastStudents from './MentorView/PastStudents';
import ParentFeedback from './MentorView/ParentFeedback';
import ViewStudentProfile from './MentorView/ViewStudentProfile';
import SessionDetails from './SessionDetails/SessionDetails';
import SessionHistory from './SessionHistory/SessionHistory';
import VideoMeeting from './VideoMeeting/VideoMeeting';

const UserView: React.FC = () => {
  const { isMentor, userId } = useAuthState();

  const impersonateId = localStorage.getItem('impersonate-user-id');

  const { data: hasRedundantEnrollments } = useGetHasRedundantEnrollments();

  const [showModal, setShowModal] = useState<boolean>(false);

  const getDismissedCountKey = (id: string) => `redundant-enrollments-dismissed-count:${id}`;

  const dismissRedundantEnrollmentsModal = () => {
    if (!userId) {
      setShowModal(false);
      return;
    }

    const currentCount = hasRedundantEnrollments?.count ?? 0;
    localStorage.setItem(getDismissedCountKey(userId), String(currentCount));
    setShowModal(false);
  };

  useEffect(() => {
    if (!isMentor || !userId) {
      setShowModal(false);
      return;
    }

    const currentCount = hasRedundantEnrollments?.count ?? 0;
    const hasResults = hasRedundantEnrollments?.result ?? false;
    const dismissedCount = Number(localStorage.getItem(getDismissedCountKey(userId)));
    const shouldShow = hasResults && dismissedCount !== currentCount;
    setShowModal(shouldShow);
  }, [hasRedundantEnrollments?.count, hasRedundantEnrollments?.result, isMentor, userId]);

  return (
    <div
      className="h-full"
      style={{
        display: 'flex',
        backgroundColor: COLORS.BLACK,
      }}
    >
      <HeaderV3>
        <div className="user-view-page-content">
          <Switch>
            <Route path={HOME}>{isMentor ? <MentorHome /> : <Home />}</Route>
            <Route path={`${COURSES}`}>
              <Courses />
            </Route>

            {isMentor && (
              <Route path={MY_ENROLLMENTS}>
                <MyEnrollments />
              </Route>
            )}

            {isMentor && (
              <Route path={MY_FLAGS}>
                <MyFlags />
              </Route>
            )}

            {isMentor && (
              <Route path={PARENT_FEEDBACK}>
                <ParentFeedback />
              </Route>
            )}

            {isMentor && (
              <Route path={MENTOR_ACTIVITY_REPORT}>
                <ActivityReport />
              </Route>
            )}

            {isMentor && (
              <Route path={PAST_STUDENTS}>
                <PastStudents />
              </Route>
            )}
            <Route path={SESSIONS}>
              <SessionHistory />
            </Route>

            <Route path={`${VIDEO_MEETING}/:meetingId`}>
              <VideoMeeting />
            </Route>

            <Route path={`${STUDENT_PROFILE}/:studentId`}>
              <ViewStudentProfile />
            </Route>

            <Route path={`${SESSION_DETAILS}/:sessionId`}>
              <SessionDetails />
            </Route>

            <Route path={KNOWLEDGE_BASE_ASSISTANT}>
              <KnowledgeBaseAssistant />
            </Route>

            <Route path={ACCOUNT}>
              <AccountPage />
            </Route>

            <Redirect to={HOME} />
          </Switch>
        </div>
      </HeaderV3>

      {/*TODO Remove this and it's usage if no longer needed*/}
      {isMentor && showModal && (
        <AgoraModal
          setModalOpen={dismissRedundantEnrollmentsModal}
          isModalOpen={showModal}
          closable={!!impersonateId}
          maskClosable={!!impersonateId}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <p style={{ fontWeight: 'bold', marginBottom: '16px' }}>Important Notice!</p>
            <p style={{ marginBottom: '16px' }}>
              For any students you've finished working with or are no longer actively mentoring,
              please proceed to the 'My Enrollments' tab and click 'End' next to their course
              enrollment. This will shift former students to the alumni section instead of being
              listed as active.
            </p>
            <p style={{ marginBottom: '32px' }}>
              If you have any questions please contact us on Discord or via email at{' '}
              <a
                style={{ color: COLORS.PRIMARY }}
                href="mailto: education@upgrade.education"
                target="_blank"
                rel="noreferrer"
              >
                education@upgrade.education
              </a>
            </p>
            <Link
              to={MY_ENROLLMENTS}
              onClick={() => {
                dismissRedundantEnrollmentsModal();
              }}
            >
              <Button>Take me there!</Button>
            </Link>
          </div>
        </AgoraModal>
      )}
    </div>
  );
};

export default UserView;
